import { useMemo } from "react";

import { Root } from "@radix-ui/react-accordion";
import { useForm } from "react-hook-form";

import { ManifestIntegrationProvider } from "src/api/connectApi/manifestTypes";
import { useConnection, useProviderManifest } from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import { Divider } from "src/base-components/Divider";
import {
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { LoadingView } from "src/base-components/LoadingView";
import { checkIfFileIsEmpty } from "src/baseConnectionNode/utils";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  ManifestConnectionNode,
  ManifestConnectionNodeData,
} from "src/constants/NodeDataTypes";
import { ConfigPane } from "src/integrationNode/editorComponents/ConfigPane";
import { InputMappings } from "src/integrationNode/editorComponents/InputMappings";
import {
  FileOutputMappings,
  OutputMappings,
} from "src/integrationNode/editorComponents/OutputMappings";
import {
  Config,
  InputMappings as InputMappingsType,
  OutputMapping,
  OutputMappings as OutputMappingsType,
} from "src/integrationNode/types";
import { ManifestTargetEnvironmentConfig } from "src/manifestConnectionNode/ManifestTargetEnvironmentConfig";
import { getDefaultManifestFileOutputMappings } from "src/manifestConnectionNode/manifestConnectionResources/DefaultResources";
import {
  ManifestConfig,
  ManifestIntegrationResource,
} from "src/manifestConnectionNode/types";
import {
  fillFilesMappingFromManifest,
  fillInputMappingFromManifest,
  fillOutputMappingFromManifest,
} from "src/manifestConnectionNode/utils";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";

type Props = {
  node: ManifestConnectionNode;
  connection: Connection;
  manifest: ManifestIntegrationProvider;
} & NodeEditorBaseProps<ManifestConnectionNodeData>;

type WrapperProps = {
  node: ManifestConnectionNode;
  workspaceId: string;
} & NodeEditorBaseProps<ManifestConnectionNodeData>;

export const ManifestConnectionNodeEditor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const baseUrl = workspace.data?.base_url;
  const connectionData = useConnection({
    baseUrl: baseUrl,
    id: node.data.connectionId,
  });

  const providerManifest = useProviderManifest(
    baseUrl,
    connectionData.data?.provider,
    connectionData.data?.manifest_version,
  );

  return (
    <LoadingView
      queryResult={[connectionData, providerManifest]}
      renderUpdated={([connection, manifest]: [
        Connection,
        ManifestIntegrationProvider,
      ]) => (
        <ManifestConnectionNodeEditorInner
          connection={connection}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          manifest={manifest}
          node={node}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

const ManifestConnectionNodeEditorInner: React.FC<Props> = ({
  node,
  connection,
  displayedError,
  immutable,
  isReactive,
  manifest,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const updateMappingInput = (inputMapping: InputMappingsType) => {
    onUpdate({ newData: { input: inputMapping } });
  };

  const updateMappingOutput = (outputMapping: OutputMappingsType) => {
    onUpdate({
      newData: { output: outputMapping },
    });
  };

  const updateMappingFiles = (filesMapping: {
    [key: string]: OutputMapping;
  }) => {
    onUpdate({
      newData: { files: filesMapping },
    });
  };

  const formProps = useForm<ManifestConfig>({
    defaultValues: node.data.config,
    ...(isReactive && { values: node.data.config }),
    mode: "onChange",
  });

  const updateConfig = (updatedConfig: Config) => {
    if (node.data.config) {
      const manifestConfig = {
        ...updatedConfig,
        environments_config: formProps.getValues().environments_config,
      };
      onUpdate({
        newData: { config: manifestConfig },
      });
    }
  };

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  node.data.input = fillInputMappingFromManifest(
    manifest,
    node.data as ManifestIntegrationResource,
  );
  const hasInputMappings = Object.values(node.data.input)
    .map((partialInputs) => Object.keys(partialInputs).length)
    .some((partialInputsLength) => partialInputsLength > 0);
  node.data.output = fillOutputMappingFromManifest(
    manifest,
    node.data as ManifestIntegrationResource,
  );

  const fileExtraction =
    manifest.resources[node.data.providerResource.resource]?.file_extraction;
  const hasFile = !!fileExtraction;

  const hasRawProviderRequestEnabled = connection.resource_configs.find(
    (resource) => resource.id === node.data.resourceConfigId,
  )?.has_raw_requests_enabled_in_resource;

  const filesFromNodeData = node.data.files;

  // We need memo because of the weird interaction between the form, JSON object and the proxy object from Mobx.
  // It causes infinite re-renders if we don't memo.
  const files = useMemo(() => {
    if (!hasFile) return {};
    const defaultFiles = getDefaultManifestFileOutputMappings(fileExtraction);
    const f =
      (checkIfFileIsEmpty(filesFromNodeData)
        ? defaultFiles
        : filesFromNodeData) || {};
    return fillFilesMappingFromManifest(f, fileExtraction);
  }, [filesFromNodeData, hasFile, fileExtraction]);

  return (
    <Root
      className={accordionRootClassName}
      defaultValue={["input", "output", "file", "config"]}
      type="multiple"
    >
      {hasInputMappings && (
        <AccordionItem
          className="pb-3"
          disabled={renderedInDiffView}
          title="Configure API request"
          value="input"
        >
          <InputMappings
            immutable={immutable}
            integrationResource={node.data}
            isReactive={isReactive}
            runFieldErrors={runFieldErrors}
            updateInputMapping={updateMappingInput}
          />
        </AccordionItem>
      )}
      <AccordionItem
        className="pb-1"
        disabled={renderedInDiffView}
        title="Configure API response"
        value="output"
      >
        <OutputMappings
          immutable={immutable}
          integrationResource={node.data}
          isReactive={isReactive}
          runFieldErrors={runFieldErrors}
          updateMappingOutput={updateMappingOutput}
        />
      </AccordionItem>
      {hasFile && (
        <AccordionItem
          className="pb-1"
          disabled={renderedInDiffView}
          title="File Handling"
          value="file"
        >
          <FileOutputMappings
            files={files}
            immutable={immutable}
            isReactive={isReactive}
            runFieldErrors={runFieldErrors}
            updateMappingFiles={updateMappingFiles}
          />
        </AccordionItem>
      )}
      <AccordionItem
        className="pb-7"
        disabled={renderedInDiffView}
        title="Advanced settings"
        value="config"
      >
        <>
          <ManifestTargetEnvironmentConfig
            config={node.data.config}
            connection={connection}
            formProps={formProps}
            immutable={immutable}
            isReactive={isReactive}
            manifest={manifest}
            onUpdate={updateConfig}
          />
          <Divider spacing="my-6" />
        </>

        <ConfigPane
          config={node.data.config}
          dataRetention={connection.data_retention}
          hasRawProviderRequestEnabled={hasRawProviderRequestEnabled}
          immutable={immutable}
          isReactive={isReactive}
          manifest={manifest}
          providerResource={node.data.providerResource}
          showTenantIDField={false}
          updateConfig={updateConfig}
        />
      </AccordionItem>
    </Root>
  );
};
